import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { CLIENTS } from "@/data/clients";

// World-atlas 110m TopoJSON hosted on jsDelivr, MIT licensed
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Custom projection centred on the UK
const PROJECTION_CONFIG = {
  scale: 2600,
  center: [-3.0, 54.5],
};

// Group clients that share a postcode/coord so we can render a "+N" pin
const groupByCoord = (list) => {
  const map = new Map();
  list.forEach((c) => {
    const key = `${c.lat.toFixed(2)}_${c.lng.toFixed(2)}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(c);
  });
  return Array.from(map.values());
};

export default function UKMap() {
  const [hovered, setHovered] = useState(null);
  const groups = groupByCoord(CLIENTS);

  return (
    <div
      data-testid="uk-client-map"
      className="relative w-full mx-auto rounded-2xl overflow-hidden border border-white/10 bg-slate-900"
      style={{ aspectRatio: "3 / 4", maxWidth: 720 }}
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={PROJECTION_CONFIG}
        width={600}
        height={800}
        style={{ width: "100%", height: "100%" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies
              // Only render UK (ISO 826) + Ireland (ISO 372) for visual context
              .filter((g) => ["826", "372"].includes(g.id))
              .map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={geo.id === "826" ? "#1e293b" : "#0f172a"}
                  stroke={geo.id === "826" ? "#22d3ee" : "#334155"}
                  strokeWidth={geo.id === "826" ? 0.8 : 0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
          }
        </Geographies>

        {groups.map((group) => {
          const primary = group[0];
          const count = group.length;
          const isHovered = hovered && hovered.slug === primary.slug;
          const label =
            count > 1
              ? `${primary.name} +${count - 1}`
              : primary.name;
          // Approximate label width so the background rect fits the text
          const labelWidth = Math.max(label.length * 4.2 + 12, 40);
          return (
            <Marker
              key={primary.slug}
              coordinates={[primary.lng, primary.lat]}
              onMouseEnter={() =>
                setHovered({ ...primary, group, count })
              }
              onMouseLeave={() => setHovered(null)}
            >
              <Link
                to={`/clients/${primary.slug}`}
                data-testid={`map-pin-${primary.slug}`}
              >
                <g style={{ cursor: "pointer" }}>
                  {/* Native accessibility tooltip */}
                  <title>{label}</title>
                  <circle
                    r={isHovered ? 9 : 5}
                    fill="#22d3ee"
                    stroke="#ffffff"
                    strokeWidth={1.5}
                    style={{
                      transition: "r 200ms ease, opacity 200ms ease",
                      filter: "drop-shadow(0 0 4px rgba(34, 211, 238, 0.6))",
                    }}
                  />
                  {count > 1 && (
                    <text
                      textAnchor="middle"
                      y={2}
                      fontSize={7}
                      fontWeight={700}
                      fill="#0f172a"
                      style={{ pointerEvents: "none" }}
                    >
                      {count}
                    </text>
                  )}
                  {/* Inline hover label, sits right next to the pin */}
                  {isHovered && (
                    <g
                      style={{ pointerEvents: "none" }}
                      transform="translate(12, -6)"
                    >
                      <rect
                        x={0}
                        y={0}
                        width={labelWidth}
                        height={16}
                        rx={4}
                        fill="#0f172a"
                        stroke="#22d3ee"
                        strokeWidth={0.6}
                      />
                      <text
                        x={6}
                        y={11}
                        fontSize={8}
                        fontWeight={600}
                        fill="#ffffff"
                        style={{ pointerEvents: "none" }}
                      >
                        {label}
                      </text>
                    </g>
                  )}
                </g>
              </Link>
            </Marker>
          );
        })}
      </ComposableMap>

      {/* Hover tooltip */}
      {hovered && (
        <div
          data-testid="map-tooltip"
          className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 z-10 max-w-md"
        >
          <div className="bg-slate-900 text-white px-4 py-3 rounded-lg shadow-2xl border border-cyan-400/40">
            {hovered.count > 1 ? (
              <>
                <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">
                  {hovered.count} clients at this location
                </div>
                <div className="text-sm text-white/80 leading-snug">
                  {hovered.group.map((c) => c.name).join(" · ")}
                </div>
              </>
            ) : (
              <>
                <div className="font-heading text-base text-white">
                  {hovered.name}
                </div>
                <div className="text-xs text-white/60">
                  {hovered.postcode}
                </div>
              </>
            )}
            <div className="text-[10px] mt-2 text-cyan-300 uppercase tracking-widest">
              Click to view
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur border border-slate-200 rounded-full px-4 py-2 flex items-center gap-2 text-xs">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full bg-cyan-400"
          style={{ boxShadow: "0 0 8px rgba(34, 211, 238, 0.7)" }}
        />
        <span className="text-slate-700 font-medium">
          Client locations
        </span>
      </div>
    </div>
  );
}
